import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl, FormattedMessage } from 'react-intl'
import { useMedia } from 'react-use'
import useBus from 'src/hooks/useBus'

import { DATE_INTL_FORMAT_WEEKDAY_MONTH_TEXT } from 'src/comps/DateIntl/constants'
import { ERROR_CONFIRMATION, STATUS_NOT_OK, STATUS_OK } from 'src/effects/ModifyDates/constants'
import { VARIANT_DEFAULT, VARIANT_FULLSCREEN } from 'client/components/Modal/constants'

import {
  STATUS_REFUND_WALLET_OK,
  STATUS_REFUND_WALLET_NOT_OK,
} from 'src/effects/RefundWallet/constants'

import {
  STATUS_REFUND_CREDIT_CARD_OK,
  STATUS_REFUND_CREDIT_CARD_NOT_OK,
} from 'src/effects/RefundCreditCard/constants'

import { BOOKING_CANCELLED, BOOKING_CONFIRMED, BOOKING_UNSOLVED } from 'src/constants/voucher'

import { displayAlertAction } from 'src/comps/AlertMessage/actions'
import { fetchRefundCreditCardRequestAction } from 'src/effects/RefundCreditCard/actions'
import { fetchRefundWalletRequestAction } from 'src/effects/RefundWallet/actions'
import { fetchVoucherRequestAction } from 'src/comps/Voucher/actions'
import { resetStatusAction } from 'src/effects/ModifyDates/actions'

import { getEmail } from 'src/comps/Voucher/selectors'
import { getStep } from 'src/effects/ModifyDates/selectors'
import { getStatus as getStatusEmail } from 'src/effects/VerifyEmail/selectors'
import { getStatus as getStatusRefundCreditCard } from 'src/effects/RefundCreditCard/selectors'
import { getStatus as getStatusRefundWallet } from 'src/effects/RefundWallet/selectors'

import { scrollToPosition } from 'lib/utils/scroll'
import { typeBookingInformation } from 'client/types/voucher'
import { typePaymentMethods } from 'client/types/checkout'

import ButtonSimple from 'src/comps/ButtonSimple'
import CancellationPolicies from 'src/comps/CancellationPolicies'
import Capacity from 'src/comps/Capacity'
import CollapsibleSection from 'client/components/CollapsibleSection/CollapsibleSection'
import DateIntl from 'src/comps/DateIntl'
import Feedback from 'client/components/FeedbackVoucher'
import IconPendingCheck from 'client/icons/PendingCheck'
import InformationBox from 'src/comps/InformationBox'
import InvoicePanel from 'src/comps/Molecules/InvoicePanel'
import Modal from 'client/components/Modal'
import ModifyDates from 'src/effects/ModifyDates'
import PaymentInformation from 'client/components/PaymentInformation'
import RoomCard from './RoomCard/RoomCard'
import SpecialRequests from 'src/effects/SpecialRequests'
import VerifyEmailModal from 'src/effects/VerifyEmail'
import DatesChangeConnected from '../DatesChangeConnected'

import { useDevice } from 'src/hooks/useDevice'

import {
  BookingInformationBase,
  BookingInformationHeader,
  BookingInformationLabel,
  BookingInformationSubtitle,
  BookingInformationTitle,
  BookingInformationValue,
  BookingInformationTitleSection,
  Column,
  FeedbackSection,
  RefundRow,
  Row,
  RowSeparator,
  ModalContent,
} from './styles'
import { breakpoint } from 'client/styles'

import messages from './messages'
import HtmlViewer from 'client/components/HtmlViewer'

const BookingInformation = ({
  bookingInformation,
  displayForm,
  paymentMethods,
  propertyId,
  purchaseId,
  rooms,
  isRefundable = false,
}) => {
  const intl = useIntl()
  const messageRef = useRef()
  const { device } = useDevice()
  const { subscribe } = useBus()

  const dispatch = useDispatch()
  const isWide = useMedia(breakpoint.tablet, device === 'desktop')

  const email = useSelector(getEmail)
  const step = useSelector(getStep)

  const statusEmail = useSelector(getStatusEmail(bookingInformation?.hash))
  const statusRefundCreditCard = useSelector(getStatusRefundCreditCard)
  const statusRefundWallet = useSelector(getStatusRefundWallet)
  const isEmailVerified = email && statusEmail === STATUS_OK

  const [open, setOpen] = useState(false)
  const [confirmExitOpen, setConfirmExitOpen] = useState(false)

  subscribe('show-change-dates-modal', () => {
    setOpen(true)
  })

  const paymentStatusFailed = bookingInformation?.paymentStatus === 'failed'
  const isCancelled = bookingInformation?.bookingStatus === BOOKING_CANCELLED
  const unconfirmedStatus = bookingInformation.bookingStatus !== BOOKING_CONFIRMED

  function closeModifyDatesModal() {
    dispatch(resetStatusAction())

    if (step === ERROR_CONFIRMATION) {
      dispatch(fetchVoucherRequestAction({ purchaseId }))
    }
  }

  function handleRefundWallet() {
    dispatch(fetchRefundWalletRequestAction({ hash: bookingInformation?.hash }))
  }

  function handleRefundCreditCard() {
    dispatch(fetchRefundCreditCardRequestAction({ hash: bookingInformation?.hash }))
  }

  function handleCloseModal() {
    setOpen(false)
    return isEmailVerified && closeModifyDatesModal
  }

  function handleExitWithoutSaving() {
    setConfirmExitOpen(false)
    setOpen(false)
    return isEmailVerified && closeModifyDatesModal
  }

  function handleContinueEditing() {
    setConfirmExitOpen(false)
  }

  useEffect(() => {
    if (statusRefundWallet === STATUS_REFUND_WALLET_OK) {
      dispatch(displayAlertAction({ messageId: 'successRefund', severity: 'success' }))
    }

    if (statusRefundWallet === STATUS_REFUND_WALLET_NOT_OK) {
      dispatch(displayAlertAction({ messageId: 'errorDefault' }))
    }
  }, [dispatch, statusRefundWallet])

  useEffect(() => {
    if (statusRefundCreditCard === STATUS_REFUND_CREDIT_CARD_OK) {
      dispatch(displayAlertAction({ messageId: 'successRefund', severity: 'success' }))
    }

    if (statusRefundCreditCard === STATUS_REFUND_CREDIT_CARD_NOT_OK) {
      dispatch(displayAlertAction({ messageId: 'errorDefault' }))
    }
  }, [dispatch, statusRefundCreditCard])

  useEffect(() => {
    if (displayForm && paymentStatusFailed) {
      scrollToPosition({ ref: messageRef })
    }
  }, [displayForm, paymentStatusFailed])

  if (!bookingInformation) {
    return null
  }

  const showRefundableBtn = isRefundable && bookingInformation.bookingStatus === BOOKING_UNSOLVED

  const adults = parseInt(bookingInformation.adults, 10)
  const children = parseInt(bookingInformation.children, 10)

  return (
    <BookingInformationBase data-testid="booking-information" isCancelled={isCancelled}>
      {bookingInformation.issues?.length > 0 && (
        <Row>
          <InformationBox
            icon={<IconPendingCheck />}
            text={intl.formatMessage(messages.cancelErrorDescription)}
            title={intl.formatMessage(messages.cancelErrorTitle)}
            status={STATUS_NOT_OK}
          />
        </Row>
      )}

      {paymentStatusFailed && !isCancelled && (
        <Row>
          <FeedbackSection ref={messageRef}>
            <Feedback
              dataTestId="payment-not-ok"
              text={[
                intl.formatMessage(messages.bookingNotPaid),
                intl.formatMessage(messages.creditCardNeeded),
                intl.formatMessage(messages.previousAttempt, {
                  card_last_digits: bookingInformation.creditCard?.number,
                }),
              ]}
              status={STATUS_NOT_OK}
            />
          </FeedbackSection>
        </Row>
      )}

      <BookingInformationTitleSection>
        <BookingInformationTitle>
          <FormattedMessage {...messages.bookingInformation} />
        </BookingInformationTitle>
      </BookingInformationTitleSection>

      {showRefundableBtn && (
        <RefundRow>
          <ButtonSimple
            onClick={handleRefundWallet}
            variant="tertiary"
            dataTestId={`refund-staywallet-${bookingInformation?.voucher}`}
            text={intl.formatMessage(messages.refundStaywallet)}
            fullWidth={!isWide}
          />

          <ButtonSimple
            onClick={handleRefundCreditCard}
            variant="primary"
            dataTestId={`refund-credit-card-${bookingInformation?.voucher}`}
            text={intl.formatMessage(messages.refundCreditCard)}
            fullWidth={!isWide}
          />
        </RefundRow>
      )}

      <Row>
        <BookingInformationHeader>
          <Row>
            <BookingInformationSubtitle>
              <FormattedMessage {...messages.bookedBy} />
            </BookingInformationSubtitle>
            <BookingInformationValue>{bookingInformation.userFullName}</BookingInformationValue>
          </Row>

          <RowSeparator>
            <BookingInformationLabel>
              <FormattedMessage {...messages.checkIn} />
            </BookingInformationLabel>

            <BookingInformationValue suppressHydrationWarning>
              <DateIntl
                value={bookingInformation.checkIn}
                format={DATE_INTL_FORMAT_WEEKDAY_MONTH_TEXT}
              />
            </BookingInformationValue>
          </RowSeparator>

          <RowSeparator>
            <BookingInformationLabel>
              <FormattedMessage {...messages.checkOut} />
            </BookingInformationLabel>

            <BookingInformationValue suppressHydrationWarning>
              <DateIntl
                value={bookingInformation.checkOut}
                format={DATE_INTL_FORMAT_WEEKDAY_MONTH_TEXT}
              />
            </BookingInformationValue>
          </RowSeparator>

          <RowSeparator>
            <BookingInformationLabel>
              <FormattedMessage {...messages.stay} />
            </BookingInformationLabel>

            <BookingInformationValue>
              <FormattedMessage
                {...messages.nightsCount}
                values={{ nights: bookingInformation.nights }}
              />
            </BookingInformationValue>
          </RowSeparator>

          <Row>
            <BookingInformationLabel>
              <FormattedMessage {...messages.guests} />
            </BookingInformationLabel>

            <Capacity
              adults={adults}
              children={children}
              childrenAges={bookingInformation.childrenAges}
              showAges={false}
              title={false}
            />
          </Row>

          {bookingInformation.isCancellable &&
            !unconfirmedStatus &&
            bookingInformation.issues?.length === 0 && (
              <Row>
                <DatesChangeConnected
                  trackingData={{
                    context: 'booking_thanks',
                    details: 'Initiate change dates/people',
                  }}
                >
                  <ButtonSimple
                    onClick={() => setOpen(!open)}
                    variant="tertiary"
                    dataTestId={`modify-dates-and-paxes-${bookingInformation.voucher}`}
                    text={intl.formatMessage(
                      rooms > 1 ? messages.modifyDates : messages.modifyDatesAndPaxes
                    )}
                  />
                </DatesChangeConnected>
              </Row>
            )}

          <Row>
            <RoomCard
              adults={adults}
              beds={bookingInformation.beds}
              boardCode={bookingInformation.boardCode}
              boardName={bookingInformation.boardName}
              children={children}
              childrenAges={bookingInformation.childrenAges}
              holders={bookingInformation.holders}
              isMobile={bookingInformation.isMobile}
              roomName={bookingInformation.roomName}
              rooms={rooms}
            />
          </Row>
        </BookingInformationHeader>

        <Column>
          <PaymentInformation
            confirmedStatus={!unconfirmedStatus}
            creditCard={bookingInformation.creditCard}
            currency={bookingInformation.currency}
            displayForm={displayForm}
            hash={bookingInformation.hash}
            isCancellable={bookingInformation.isCancellable}
            isCreditCard={bookingInformation.creditCard !== null}
            isPayable={bookingInformation.isPayable}
            language={bookingInformation.language}
            needToPay={paymentStatusFailed}
            paymentMethods={paymentMethods}
            pendingAmount={bookingInformation.pendingAmount}
            purchaseId={purchaseId}
            ticketsPaid={bookingInformation.ticketsPaid}
            totalPaid={bookingInformation.totalPaid}
            partialAmounts={bookingInformation.partialAmounts}
            bookingHash={bookingInformation.hash}
          />

          <CancellationPolicies
            cancellationPolicies={bookingInformation.cancellationPolicies}
            pendingAmount={bookingInformation.pendingAmount}
            generatedWallet={bookingInformation.generatedWallet}
            hash={bookingInformation.hash}
            rateName={bookingInformation.rateName}
            internal={bookingInformation.internal}
            confirmedStatus={!unconfirmedStatus}
            voucherIssues={bookingInformation.issues?.length > 0}
            idBooking={bookingInformation.idBooking}
            isRefundable={isRefundable}
          />

          {!unconfirmedStatus && (
            <InvoicePanel
              date={bookingInformation.invoice?.datetime}
              hash={bookingInformation.hash}
              status={bookingInformation.invoice?.status}
            />
          )}
        </Column>
      </Row>

      {!unconfirmedStatus && (
        <Row className="noPrint">
          <SpecialRequests
            specialRequests={bookingInformation.specialRequests}
            hash={bookingInformation.hash}
          />
        </Row>
      )}

      {bookingInformation.comments && (
        <Row className="noPrint">
          <CollapsibleSection title={intl.formatMessage(messages.additionalInformation)} fullWidth>
            <HtmlViewer html={bookingInformation.comments} />
          </CollapsibleSection>
        </Row>
      )}

      <ModalContent>
        <Modal
          show={open}
          minWidth="720px"
          variant={isWide ? VARIANT_DEFAULT : VARIANT_FULLSCREEN}
          handleClose={handleCloseModal}
          onBeforeClose={() => {
            if (isEmailVerified && step) {
              setConfirmExitOpen(true)
              return false
            }
            return true
          }}
        >
          {isEmailVerified ? (
            <>
              <ModifyDates.ConfirmExit
                open={confirmExitOpen}
                onClose={() => setConfirmExitOpen(false)}
                onContinueEditing={handleContinueEditing}
                onExitWithoutSaving={handleExitWithoutSaving}
              />
              <ModifyDates
                bookingInformation={bookingInformation}
                propertyId={propertyId}
                sellingRate={bookingInformation.sellingRate}
                rooms={rooms}
              />
            </>
          ) : (
            <VerifyEmailModal
              buttonText={intl.formatMessage(
                rooms > 1 ? messages.modifyDates : messages.modifyDatesAndPaxes
              )}
              hash={bookingInformation?.hash}
            />
          )}
        </Modal>
      </ModalContent>
    </BookingInformationBase>
  )
}

BookingInformation.propTypes = {
  bookingInformation: typeBookingInformation,
  displayForm: PropTypes.bool,
  paymentMethods: typePaymentMethods,
  propertyId: PropTypes.string,
  purchaseId: PropTypes.string,
  rooms: PropTypes.number,
  isRefundable: PropTypes.bool,
}

export default BookingInformation
